import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectHexes,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPhase,
  selectRound,
  selectSharedGameState,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calcPlayerIncomeBreakdown, INCOME_ADDITION_IDS, INCOME_RESOURCES} from "../../gameState/income/calcPlayerIncomeBreakdown";
import {getRulesGameType} from "../../gameState/getRulesGameType";
import {getRuinsIncomeRoundLimit, getRuinsIncreasedIncomeStartRound} from "../../gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {checkIfCitylessEconomyRule} from "../../gameState/income/checkIfCitylessEconomyRule";
import {KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {ALL_BUILDING_CONFIGS, KEY_POINT_CONFIGS} from "../../../configs/tokens";
import {PHASES} from "../../../constants/phases";
import {
  CONSTRUCTION_ACTIONS_SOURCE_IDS,
  countWorkingPalaces,
  getConstructionActionsSources,
  sumConstructionActionsSources
} from "../../gameState/construction/getConstructionActionsSources";
import {calcUsedConstructionActions} from "../../gameState/construction/calcUsedConstructionActions";
import {CONSTRUCTION_ACTION_TYPES} from "../../../constants/construction";
import {checkIfCentralQuarter} from "../../buildings/checkIfCentralQuarter";
import {MINE_EXPLORATION_CONFIG} from "../../../configs/factions/dwarves";
import {getPlayerNextPolicy} from "../../policy/getPlayerNextPolicy";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {getUnitTypeConfig, UNIT_TYPES} from "../../../configs/units";
import {getSameTypeUnitLimit} from "../../gameState/recruitment/checkSameTypeUnitRecruitmentLimit";
import {getPlayerUnits} from "../getPlayerUnits";
import {getPlayerFinalWinPoints} from "../../gameEnd/buildGameResults";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {ENLIGHTENED_COINS_TO_WIN_POINTS_CONVERSION} from "../../../configs/factions/enlightened";
import {COINS_TO_WIN_POINTS_CONVERSION} from "../../../configs/game";
import {POLICY_TOTALITARIANISM_MILITARY_BUILDINGS_WIN_POINTS_RATIO} from "../../../configs/policy/totalitarianism";

// Что показывает блок действий строительства
export const CONSTRUCTION_ACTIONS_MODES = Object.freeze({
  // Идет фаза строительства: сколько действий в этой фазе и на что потрачены
  CURRENT: 'current',
  // Строительство этого раунда впереди (снабжение, подготовка): прогноз
  THIS_ROUND: 'this_round',
  // Строительство этого раунда прошло (война): прогноз на следующий раунд
  NEXT_ROUND: 'next_round',
});

// Порядок групп дохода в панели: монеты — рудники, гильдии; очки — руины,
// храмы, кварталы Гегемонизма
const SOURCE_TOKEN_ORDER = [
  KEY_POINT_TOKENS.SUPER_MINE,
  KEY_POINT_TOKENS.MINE,
  SPECIAL_BUILDING_TOKENS.GUILD,
  KEY_POINT_TOKENS.SUPER_RUINS,
  KEY_POINT_TOKENS.RUINS,
  SPECIAL_BUILDING_TOKENS.TEMPLE,
  TOKENS.QUARTER,
];

// Название группы: в единственном числе, во множественном — только у группы
// из нескольких («Рудники ×2»)
const SOURCE_LABELS = {
  [KEY_POINT_TOKENS.MINE]: {one: 'Рудник', many: 'Рудники'},
  [KEY_POINT_TOKENS.SUPER_MINE]: {one: 'Великий рудник', many: 'Великие рудники'},
  [KEY_POINT_TOKENS.RUINS]: {one: 'Руины', many: 'Руины'},
  [KEY_POINT_TOKENS.SUPER_RUINS]: {one: 'Великие руины', many: 'Великие руины'},
  [SPECIAL_BUILDING_TOKENS.GUILD]: {one: 'Гильдия', many: 'Гильдии'},
  [SPECIAL_BUILDING_TOKENS.TEMPLE]: {one: 'Храм', many: 'Храмы'},
  [TOKENS.QUARTER]: {one: 'Квартал (Гегемонизм)', many: 'Кварталы (Гегемонизм)'},
};

const getSourceLabel = (token, count) => {
  const labels = SOURCE_LABELS[token];
  if (!labels) {
    return token;
  }
  return count > 1 ? labels.many : labels.one;
};

/**
 * Слагаемые дохода одного источника — подписи к числам. Базовая часть
 * показывается всегда, надбавки — только ненулевые
 */
const getSourceParts = (token, details) => {
  if (!details) {
    return [];
  }
  const parts = [];
  const add = (label, amount, isAlwaysShown = false) => {
    if (amount > 0 || isAlwaysShown) {
      parts.push({label, amount});
    }
  };

  switch (token) {
    case KEY_POINT_TOKENS.MINE:
    case KEY_POINT_TOKENS.SUPER_MINE:
      add('базовый', details.base, true);
      add('Серваризм: рядом своя казарма', details.servarianismBarracks);
      add('Серваризм: рядом крупный отряд', details.servarianismUnit);
      break;
    case KEY_POINT_TOKENS.RUINS:
    case KEY_POINT_TOKENS.SUPER_RUINS:
      add(details.isIncreased ? 'увеличенный (последние раунды)' : 'базовый', details.base, true);
      add('Фанатизм', details.fanaticism);
      break;
    case SPECIAL_BUILDING_TOKENS.GUILD:
      add('базовый', details.base, true);
      add('вражеские города в зоне влияния', details.influence);
      add('Вентори', details.ventory);
      add('Олигархия', details.oligarchy);
      break;
    case SPECIAL_BUILDING_TOKENS.TEMPLE:
      add('базовый', details.base, true);
      add('вражеские города в зоне влияния', details.influence);
      add('Вентори', details.ventory);
      add('Гегемонизм', details.hegemonism);
      add('Теократия', details.theocracy);
      break;
    case TOKENS.QUARTER:
      add('бонусная ячейка Гегемонизма', details.hegemonism, true);
      break;
    default:
      add('доход', details.total, true);
  }
  return parts;
};

/**
 * Источники дохода одного ресурса, сгруппированные по виду и размеру дохода:
 * «Гильдии: 2 по +3 (1 базовый + 2 за вражеские города)». Заблокированные —
 * своей группой с нулевым доходом
 */
const groupIncomeSources = (sources, resource) => {
  const groups = new Map();
  for (const source of sources) {
    if (source.resource !== resource) {
      continue;
    }
    const key = `${source.token}|${source.isBlocked ? 'blocked' : JSON.stringify(source.details)}`;
    const group = groups.get(key);
    if (group) {
      group.count += 1;
      group.total += source.amount;
    } else {
      groups.set(key, {
        key,
        token: source.token,
        image: getTokenImage(source.token),
        isBlocked: source.isBlocked,
        count: 1,
        amountEach: source.amount,
        total: source.amount,
        parts: getSourceParts(source.token, source.details),
      });
    }
  }

  return [...groups.values()]
    .map((group) => ({...group, label: getSourceLabel(group.token, group.count)}))
    .sort((a, b) => (
      SOURCE_TOKEN_ORDER.indexOf(a.token) - SOURCE_TOKEN_ORDER.indexOf(b.token)
      || Number(a.isBlocked) - Number(b.isBlocked)
      || b.amountEach - a.amountEach
    ));
};

// Доплаты к доходу — и монетами, и очками победы (очки дает бонус
// «божество», см. calcPlayerIncomeBreakdown)
const ADDITION_LABELS = {
  [INCOME_ADDITION_IDS.LEPRECHAUNS]: 'Способность фракции (Лепреконы)',
  [INCOME_ADDITION_IDS.PLAYER_BONUS]: 'Бонус к доходу',
};

const withAdditionLabels = (additions) => (additions || []).map((addition) => ({
  ...addition,
  label: ADDITION_LABELS[addition.id] || addition.id,
}));

const CONSTRUCTION_SOURCE_LABELS = {
  [CONSTRUCTION_ACTIONS_SOURCE_IDS.BASE]: 'Базовые действия',
  [CONSTRUCTION_ACTIONS_SOURCE_IDS.FIRST_ROUND]: 'Первый раунд',
  [CONSTRUCTION_ACTIONS_SOURCE_IDS.PALACES]: 'Рабочие дворцы',
  [CONSTRUCTION_ACTIONS_SOURCE_IDS.INDUSTRIALISM]: 'Индустриализм',
  [CONSTRUCTION_ACTIONS_SOURCE_IDS.ELEMENTALS]: 'Способность фракции (Элементали)',
  [CONSTRUCTION_ACTIONS_SOURCE_IDS.PLAYER_BONUS]: 'Бонус к действиям',
};

const withSourceLabels = (sources) => sources.map((source) => ({
  ...source,
  label: CONSTRUCTION_SOURCE_LABELS[source.id] || source.id,
}));

// Жетон строения или ключевой точки — картинка рядом с названием группы
const getTokenImage = (token) => {
  const config = ALL_BUILDING_CONFIGS.find((item) => item.id === token)
    || KEY_POINT_CONFIGS.find((item) => item.id === token);
  return config ? (config.buttonImage || config.image) : null;
};

const getTokenLabel = (token) => {
  if (checkIfCentralQuarter(token)) {
    return 'Центральный квартал';
  }
  if (token === MINE_EXPLORATION_CONFIG.id) {
    return MINE_EXPLORATION_CONFIG.label;
  }
  return ALL_BUILDING_CONFIGS.find((config) => config.id === token)?.label
    || KEY_POINT_CONFIGS.find((config) => config.id === token)?.label
    || token;
};

/**
 * На что потрачены действия фазы строительства: строения и ремонты этого
 * хода (бесплатные — отдельно, действия они не тратят), перехват инициативы
 * и обновление рынка политик
 */
const buildSpentActions = (player, pendingBuildings, nextInitiativePlayerId, isInterceptedWithIsolationism) => {
  const items = pendingBuildings.map((building) => {
    const isRepair = building.type === CONSTRUCTION_ACTION_TYPES.BUILDING_REPAIRMENT;
    return {
      key: `${building.hexKey}_${building.timestamp}`,
      label: isRepair ? `Ремонт: ${building.tokenLabel || 'строение'}` : getTokenLabel(building.token),
      price: isRepair ? null : (building.price ?? null),
      isFree: Boolean(building.isFreeAction),
    };
  });

  if (nextInitiativePlayerId === player.id) {
    items.push({
      key: 'initiative',
      label: isInterceptedWithIsolationism ? 'Перехват инициативы (Изоляционизм)' : 'Перехват инициативы',
      price: null,
      isFree: Boolean(isInterceptedWithIsolationism),
    });
  }
  if (player.isClearedPolicyMarketThisTurn) {
    items.push({key: 'market', label: 'Обновление рынка политик', price: null, isFree: false});
  }

  return items;
};

const buildConstructionActions = (state, player, pendingBuildings) => {
  const phase = selectPhase(state);
  const round = selectRound(state);
  const gameType = getRulesGameType(selectSharedGameState(state));
  const countPalaces = (palacesPlayer) => countWorkingPalaces(
    palacesPlayer, selectCitiesCache(state) || {}, selectUnits(state) || [],
    selectSpecialBuildings(state), selectBuildingsDamage(state)
  );

  if (phase === PHASES.CONSTRUCTION) {
    const total = player.constructionPhaseActions || 0;
    // Дворцы считались на начало фазы: их число — остаток от итога, а не
    // текущее (дворец могли заблокировать уже после начала фазы)
    const otherSources = getConstructionActionsSources(player, {round, gameType, workingPalacesCount: 0});
    const palaces = total - sumConstructionActionsSources(otherSources);
    const sources = palaces > 0
      ? [...otherSources, {id: CONSTRUCTION_ACTIONS_SOURCE_IDS.PALACES, amount: palaces}]
      : otherSources;
    const nextInitiativePlayerId = selectNextInitiativePlayerId(state);
    const isInterceptedWithIsolationism = selectIsInitiativeInterceptedWithIsolationism(state);
    const used = calcUsedConstructionActions(player, pendingBuildings, nextInitiativePlayerId, isInterceptedWithIsolationism);

    return {
      mode: CONSTRUCTION_ACTIONS_MODES.CURRENT,
      // В чужой ход строительства шапка показывает план игрока
      // (selectConstructionPlanStatsView) — его строения не потрачены, а
      // запланированы
      isPlan: pendingBuildings.length > 0 && pendingBuildings !== selectPendingBuildings(state),
      total,
      sources: withSourceLabels(sources),
      used,
      remaining: Math.max(0, total - used),
      spent: buildSpentActions(player, pendingBuildings, nextInitiativePlayerId, isInterceptedWithIsolationism),
    };
  }

  // Строительство этого раунда прошло — следующего в последнем раунде нет
  if (phase === PHASES.WAR && checkIfFinalRound(state)) {
    return null;
  }

  const isNextRound = phase === PHASES.WAR;
  // К следующему раунду купленная политика заменит текущую
  const nextPolicy = isNextRound ? getPlayerNextPolicy(player) : null;
  const forecastPlayer = nextPolicy ? {...player, policy: nextPolicy} : player;
  const forecastRound = isNextRound ? round + 1 : Math.max(1, round || 0);
  const sources = getConstructionActionsSources(forecastPlayer, {
    round: forecastRound,
    gameType,
    workingPalacesCount: countPalaces(forecastPlayer),
  });

  return {
    mode: isNextRound ? CONSTRUCTION_ACTIONS_MODES.NEXT_ROUND : CONSTRUCTION_ACTIONS_MODES.THIS_ROUND,
    total: sumConstructionActionsSources(sources),
    sources: withSourceLabels(sources),
  };
};

/**
 * Отряды игрока по типам (в порядке конфига): сколько отрядов, лимит
 * одновременных отрядов типа и размер каждого
 */
const buildUnits = (player, units) => {
  const playerUnits = getPlayerUnits(player, units || []);
  const byType = Object.values(UNIT_TYPES)
    .map((unitType) => {
      const typeUnits = playerUnits
        .filter((unit) => unit.type === unitType.id)
        .map((unit) => ({id: unit.id, size: unit.size, maxSize: unit.maxSize ?? unitType.maxSize}))
        .sort((a, b) => b.size - a.size);
      return {
        typeId: unitType.id,
        dice: unitType.label.replace('Отряд ', ''),
        label: getUnitTypeConfig(unitType.id).description,
        count: typeUnits.length,
        limit: getSameTypeUnitLimit(player, unitType.id),
        units: typeUnits,
      };
    })
    .filter((type) => type.count > 0);

  return {
    count: playerUnits.length,
    totalSize: playerUnits.reduce((sum, unit) => sum + (unit.size || 0), 0),
    byType,
  };
};

/**
 * Все, из чего складываются статы игрока, — для панели статов в шапке:
 * доход монет и очков по источникам, действия строительства, отряды и
 * итоговые очки, если бы партия закончилась сейчас
 *
 * @param {Object} state - redux-состояние
 * @param {Object} player - чьи статы (selectHeaderOwnPlayerView)
 * @param {Array} pendingBuildings - строения его хода (оттуда же)
 */
export const buildPlayerStatsView = (state, player, pendingBuildings = []) => {
  const gameState = selectSharedGameState(state);
  const round = selectRound(state);
  const ruinsIncomeRoundLimit = getRuinsIncomeRoundLimit(gameState);
  const units = selectUnits(state) || [];

  const breakdown = calcPlayerIncomeBreakdown(player, {
    citiesCache: selectCitiesCache(state) || {},
    units,
    hexes: selectHexes(state),
    specialBuildings: selectSpecialBuildings(state),
    round,
    buildingsDamage: selectBuildingsDamage(state),
    gameType: getRulesGameType(gameState),
    ruinsIncomeRoundLimit,
    isCitylessEconomyRule: checkIfCitylessEconomyRule(gameState),
  });

  const ruinsIncreasedStartRound = getRuinsIncreasedIncomeStartRound(ruinsIncomeRoundLimit);
  const coinsRate = isPlayerFaction(player, FACTIONS.ENLIGHTENED)
    ? ENLIGHTENED_COINS_TO_WIN_POINTS_CONVERSION
    : COINS_TO_WIN_POINTS_CONVERSION;

  return {
    coins: {
      current: player.coins,
      income: breakdown.coinsIncome,
      citiesCount: breakdown.citiesCount,
      basic: breakdown.basicCoins,
      additions: withAdditionLabels(breakdown.additions),
      groups: groupIncomeSources(breakdown.sources, INCOME_RESOURCES.COINS),
    },
    winPoints: {
      current: player.winPoints,
      income: breakdown.winPointsIncome,
      // Очки к доходу от бонуса — тоже только пока есть хотя бы один город
      additions: withAdditionLabels(breakdown.winPointsAdditions),
      groups: groupIncomeSources(breakdown.sources, INCOME_RESOURCES.WIN_POINTS),
      totalitarianism: breakdown.totalitarianism && {
        ...breakdown.totalitarianism,
        ratio: POLICY_TOTALITARIANISM_MILITARY_BUILDINGS_WIN_POINTS_RATIO,
      },
      // С какого раунда руины приносят больше (null — окна нет)
      ruinsIncreasedStartRound,
    },
    constructionActions: buildConstructionActions(state, player, pendingBuildings),
    units: buildUnits(player, units),
    finalWinPoints: {
      total: getPlayerFinalWinPoints(player),
      coinsRate,
    },
  };
};
